'use client';

/**
 * The building blocks of the myfitvent sign in screen: the header, the
 * footer, the picture panel on sign up, and the field, button and error
 * pieces the steps share. Colours come only from the --fvd-* tokens, so the
 * page follows the theme. Anything with a hover, focus or breakpoint state is
 * a Tailwind class rather than an inline style, because an inline style would
 * outrank the state and the breakpoint.
 */

import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { FitventMark } from '@/components/design/FitventMark';
import { MyHyroxLockup } from '@/components/brand-preview/HyroxWordmark';
import type { BrandPreviewId } from '@/lib/brand-preview/shared';

/* ── Header ─────────────────────────────────────────────────────────────── */

export function AuthHeader({
  brandPreview,
  prompt,
  action,
}: {
  brandPreview: BrandPreviewId | null;
  /** The words before the switch button, e.g. "New to myfitvent?" */
  prompt?: ReactNode;
  /** The switch button itself. Omitted once the athlete is signed in. */
  action?: { label: string; onClick: () => void };
}) {
  return (
    <header
      className="flex items-center gap-3 px-4 py-4 min-[600px]:gap-4 min-[600px]:px-7 min-[600px]:py-5"
      style={{ flex: 'none' }}
    >
      <Link
        href="/"
        aria-label={brandPreview === 'hyrox' ? 'myHYROX home' : 'myfitvent home'}
        style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', flex: 'none' }}
      >
        {brandPreview === 'hyrox' ? (
          <span style={{ color: 'var(--fvd-ink)' }}>
            <MyHyroxLockup size={15} />
          </span>
        ) : (
          <>
            <FitventMark size={20} />
            {/* The class carries the face, weight and tracking; only the size
                differs from the 15px app header, so it is the one override. */}
            <span className="fvd-wordmark" style={{ fontSize: 17 }}>
              <span className="fvd-wordmark-my">my</span>fitvent
            </span>
          </>
        )}
      </Link>
      <span style={{ flex: 1 }} />
      {prompt && (
        <span
          className="truncate max-[400px]:hidden"
          style={{ minWidth: 0, fontSize: 13, color: 'var(--fvd-ink-secondary)' }}
        >
          {prompt}
        </span>
      )}
      {/* Holds the header's height once the switch goes, so the wordmark
          does not jump when the athlete is signed in. */}
      {!action && <span aria-hidden="true" className="h-[44px] min-[768px]:h-[36px]" />}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          // 44 tall on a phone (a tap target), 36 from 768 up, as on organiser sign in.
          className="fvd-hoverable h-[44px] min-[768px]:h-[36px]"
          style={{
            flex: 'none',
            padding: '0 13px',
            border: '1px solid var(--fvd-border-control)',
            borderRadius: 8,
            background: 'var(--fvd-canvas)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--fvd-ink)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {action.label}
        </button>
      )}
    </header>
  );
}

/* ── Footer ─────────────────────────────────────────────────────────────── */

/** The footer links: a 44px tap target on a phone, plain text from 768 up. */
const footerLinkClass = 'hover:underline inline-flex items-center max-[767px]:min-h-[44px]';

export function AuthFooter() {
  const linkStyle = { color: 'var(--fvd-ink-muted)', textDecoration: 'none' } as const;
  return (
    <footer
      className="flex flex-wrap items-center gap-[18px] px-4 py-5 min-[600px]:px-7"
      style={{ flex: 'none', fontSize: 12.5, color: 'var(--fvd-ink-muted)' }}
    >
      <span>© {new Date().getFullYear()} Fitvent</span>
      <span style={{ flex: 1 }} />
      <Link href="/terms" style={linkStyle} className={footerLinkClass}>
        Terms
      </Link>
      <Link href="/privacy" style={linkStyle} className={footerLinkClass}>
        Privacy
      </Link>
    </footer>
  );
}

/* ── The picture panel (sign up only, hidden at 980px and below) ────────── */

export function AthleteArtPanel() {
  return (
    <div
      className="hidden min-[981px]:flex"
      style={{
        flexDirection: 'column',
        alignSelf: 'stretch',
        minHeight: 560,
        borderRadius: '16px 0 0 16px',
        background: 'var(--fvd-switcher-bg)',
        overflow: 'hidden',
        padding: '44px 0 0 44px',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)' }}>Every race, in one place</div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--fvd-ink-secondary)',
          marginTop: 5,
          maxWidth: '40ch',
          lineHeight: 1.55,
        }}
      >
        Tickets, start times, results and what to bring, for every event you enter.
      </div>
      <Image
        src="/auth/auth-athlete.png"
        alt="The myfitvent race ticket"
        width={2014}
        height={1766}
        sizes="(min-width: 981px) 70vw, 1px"
        loading="eager"
        style={{
          display: 'block',
          width: '125%',
          maxWidth: 'none',
          height: 'auto',
          marginTop: 30,
          borderRadius: '12px 0 0 0',
          boxShadow: 'var(--fvd-shadow-modal), 0 0 0 1px var(--fvd-hairline)',
        }}
      />
    </div>
  );
}

/* ── Type ───────────────────────────────────────────────────────────────── */

export function AuthTitle({ children, large, gap = 0 }: { children: ReactNode; large?: boolean; gap?: number }) {
  return (
    <h1
      className={large ? 'text-[28px] min-[600px]:text-[34px]' : 'text-[24px]'}
      style={{
        margin: `${gap}px 0 0`,
        fontWeight: 600,
        letterSpacing: large ? '-.032em' : '-.028em',
        lineHeight: large ? 1.12 : 1.2,
        textWrap: 'balance',
        color: 'var(--fvd-ink)',
      }}
    >
      {children}
    </h1>
  );
}

export function AuthIntro({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        margin: '9px 0 0',
        fontSize: 14,
        color: 'var(--fvd-ink-secondary)',
        lineHeight: 1.55,
        textWrap: 'pretty',
      }}
    >
      {children}
    </p>
  );
}

/** The round or square teal tile above the code and done steps. */
export function AuthIconTile({ children, round }: { children: ReactNode; round?: boolean }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        borderRadius: round ? '50%' : 10,
        background: 'var(--fvd-teal-tint)',
        color: 'var(--fvd-teal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

/* ── Fields ─────────────────────────────────────────────────────────────── */

/**
 * Border and focus ring as classes: the ring and the teal edge are focus
 * states, and an inline border colour would beat them.
 */
export function fieldClass(invalid: boolean): string {
  return [
    'w-full box-border outline-none border transition-[border-color,box-shadow] duration-100',
    // An invalid field keeps its red edge while focused, so the error and
    // the field it belongs to stay visibly tied together.
    invalid
      ? 'border-[color:var(--fvd-danger)]'
      : 'border-[color:var(--fvd-border-control)] focus:border-[color:var(--fvd-teal)]',
    'focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--fvd-teal)_14%,transparent)]',
    'placeholder:text-[color:var(--fvd-ink-disabled)]',
  ].join(' ');
}

export function FieldMessage({ id, tone, children }: { id?: string; tone: 'error' | 'hint'; children: ReactNode }) {
  return (
    <div
      id={id}
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        fontSize: 12.5,
        lineHeight: 1.5,
        marginTop: 7,
        color: tone === 'error' ? 'var(--fvd-danger)' : 'var(--fvd-ink-muted)',
      }}
    >
      {children}
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────── */

/**
 * The full-width primary. When it cannot be used yet it goes grey (not
 * faded teal), and whatever sits beside it says what is missing.
 */
export function PrimaryButton({
  children,
  disabled,
  loading,
  isMobile,
  type = 'submit',
  onClick,
  marginTop = 16,
}: {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  isMobile: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
  marginTop?: number;
}) {
  const off = disabled || loading;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={off}
      aria-busy={loading || undefined}
      className={off ? undefined : 'fvd-hoverable-solid'}
      style={{
        width: '100%',
        height: isMobile ? 48 : 42,
        marginTop,
        border: 'none',
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        cursor: off ? 'not-allowed' : 'pointer',
        // A request in flight keeps the teal: the action was valid, it is
        // just busy. Only "not ready yet" turns the button grey.
        background: disabled && !loading ? 'var(--fvd-neutral-chip)' : 'var(--fvd-teal-solid)',
        color: disabled && !loading ? 'var(--fvd-ink-disabled)' : 'var(--fvd-teal-solid-on)',
      }}
    >
      {loading && <Loader2 className="animate-spin" width={15} height={15} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** An inline text action ("Send it again", "Use a different email"). */
export function TextAction({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // A 44px tap target on a phone; inline-flex keeps it on the text's baseline.
      className="fvd-link inline-flex items-center max-[767px]:min-h-[44px]"
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 500,
        color: disabled ? 'var(--fvd-ink-disabled)' : 'var(--fvd-ink)',
        textDecoration: disabled ? 'none' : undefined,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/* ── The box below the form ─────────────────────────────────────────────── */

export function OrganiserNote() {
  return (
    <Link
      href="/sign-up"
      className="group"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        marginTop: 26,
        // Flush with the form's left edge, like every field above it.
        padding: '14px 0 0',
        borderTop: '1px solid var(--fvd-hairline)',
        textDecoration: 'none',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)' }}>
          Running events?
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 12.5,
            color: 'var(--fvd-ink-muted)',
            marginTop: 2,
            lineHeight: 1.5,
          }}
        >
          Organiser accounts are separate, and we check every organiser first.
        </span>
      </span>
      <span
        className="underline decoration-[var(--fvd-border-control)] decoration-1 underline-offset-[3px] group-hover:decoration-current"
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--fvd-ink)',
          whiteSpace: 'nowrap',
        }}
      >
        Fitvent for organisers
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.1"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12h13" />
          <path d="m12.5 6 6 6-6 6" />
        </svg>
      </span>
    </Link>
  );
}
